import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Checkbox } from '@/components/ui/checkbox';
import Layout from '@/components/Layout';
import { toast } from 'sonner';
import { MapPin, Navigation, Plus, GripVertical, Check, X, Clock, Camera, StickyNote, Trash2, Play, CheckCircle, Calendar, Search, Pencil, UserPlus, UserMinus, FileText } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Tours({ token, user, onLogout }) {
  const navigate = useNavigate();
  const [tours, setTours] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [filteredCustomers, setFilteredCustomers] = useState([]);
  const [selectedTour, setSelectedTour] = useState(null);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [tourToEdit, setTourToEdit] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [tourToDelete, setTourToDelete] = useState(null);
  const [newTourName, setNewTourName] = useState('');
  const [editTourName, setEditTourName] = useState('');
  const [selectedCustomers, setSelectedCustomers] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [locationPermission, setLocationPermission] = useState(null);
  const [currentLocation, setCurrentLocation] = useState(null);
  const [customerSearchTerm, setCustomerSearchTerm] = useState('');
  const [selectedLetter, setSelectedLetter] = useState('all');
  const [featureSettings, setFeatureSettings] = useState({
    orders_enabled: true
  });
  const [deliveryNotes, setDeliveryNotes] = useState([]);
  const [orders, setOrders] = useState([]);

  const alphabet = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('');

  useEffect(() => {
    fetchTours();
    fetchCustomers();
    fetchFeatureSettings();
    requestLocationPermission();
    fetchDeliveryNotes();
    fetchOrders(); // Immer laden, die Anzeige wird durch featureSettings gesteuert
  }, []);

  useEffect(() => {
    // Load suppliers only if feature is enabled
    if (featureSettings.orders_enabled) {
      fetchSuppliers();
    }
  }, [featureSettings]);

  useEffect(() => {
    // Kombiniere Kunden und Lieferanten in eine Liste (nur wenn Feature aktiviert)
    const customersWithType = customers.map(c => ({ ...c, contactType: 'customer' }));
    const suppliersWithType = featureSettings.orders_enabled 
      ? suppliers.map(s => ({ ...s, contactType: 'supplier' }))
      : [];
    let filtered = [...customersWithType, ...suppliersWithType];
    
    // Alphabetisch sortieren
    filtered = filtered.sort((a, b) => 
      a.company_name.localeCompare(b.company_name)
    );
    
    // Filter nach Suchbegriff
    if (customerSearchTerm) {
      filtered = filtered.filter(contact =>
        contact.company_name.toLowerCase().includes(customerSearchTerm.toLowerCase()) ||
        (contact.city && contact.city.toLowerCase().includes(customerSearchTerm.toLowerCase())) ||
        (contact.street && contact.street.toLowerCase().includes(customerSearchTerm.toLowerCase()))
      );
    }
    
    // Filter nach Buchstaben
    if (selectedLetter !== 'all') {
      filtered = filtered.filter(contact => 
        contact.company_name.toUpperCase().startsWith(selectedLetter)
      );
    }
    
    setFilteredCustomers(filtered);
  }, [customerSearchTerm, selectedLetter, customers, suppliers]);

  const requestLocationPermission = async () => {
    if ('geolocation' in navigator) {
      try {
        const permission = await navigator.permissions.query({ name: 'geolocation' });
        setLocationPermission(permission.state);
        
        if (permission.state === 'granted') {
          getCurrentLocation();
        } else if (permission.state === 'prompt') {
          // Ask user
          const result = await new Promise((resolve) => {
            toast('Möchten Sie Ihren Standort verwenden?', {
              action: {
                label: 'Ja',
                onClick: () => resolve(true)
              },
              cancel: {
                label: 'Nein',
                onClick: () => resolve(false)
              },
              duration: 10000
            });
          });
          
          if (result) {
            getCurrentLocation();
          }
        }
      } catch (error) {
        console.error('Location permission error:', error);
      }
    }
  };

  const getCurrentLocation = () => {
    navigator.geolocation.getCurrentPosition(
      (position) => {
        setCurrentLocation({
          latitude: position.coords.latitude,
          longitude: position.coords.longitude
        });
        setLocationPermission('granted');
      },
      (error) => {
        console.error('Location error:', error);
        toast.error('Standort konnte nicht ermittelt werden');
      }
    );
  };

  const calculateDistance = (lat1, lon1, lat2, lon2) => {
    // Haversine formula to calculate distance in km
    const R = 6371;
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLon = (lon2 - lon1) * Math.PI / 180;
    const a = 
      Math.sin(dLat/2) * Math.sin(dLat/2) +
      Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
      Math.sin(dLon/2) * Math.sin(dLon/2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
    return R * c;
  };

  const fetchTours = async () => {
    try {
      const response = await axios.get(`${API}/tours`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTours(response.data);
    } catch (error) {
      toast.error('Fehler beim Laden der Touren');
    }
  };

  const fetchCustomers = async () => {
    try {
      const response = await axios.get(`${API}/customers`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCustomers(response.data.filter(c => c.company_name && c.company_name.trim()));
    } catch (error) {
      toast.error('Fehler beim Laden der Kunden');
    }
  };

  const fetchSuppliers = async () => {
    try {
      const response = await axios.get(`${API}/suppliers`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSuppliers(response.data.filter(s => s.company_name && s.company_name.trim()));
    } catch (error) {
      toast.error('Fehler beim Laden der Lieferanten');
    }
  };

  const fetchDeliveryNotes = async () => {
    try {
      const response = await axios.get(`${API}/delivery-notes`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDeliveryNotes(response.data);
    } catch (error) {
      console.error('Fehler beim Laden der Lieferscheine:', error);
    }
  };

  const fetchOrders = async () => {
    try {
      const response = await axios.get(`${API}/orders`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setOrders(response.data);
    } catch (error) {
      console.error('Fehler beim Laden der Bestellungen:', error);
    }
  };

  const fetchFeatureSettings = async () => {
    try {
      const response = await axios.get(`${API}/settings/features`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setFeatureSettings(response.data);
    } catch (error) {
      console.error('Fehler beim Laden der Feature-Einstellungen:', error);
    }
  };

  const handleCreateTour = async (e) => {
    e.preventDefault();
    
    if (!newTourName.trim()) {
      toast.error('Bitte geben Sie einen Tour-Namen ein');
      return;
    }
    
    if (selectedCustomers.length === 0) {
      toast.error('Bitte wählen Sie mindestens einen Kunden oder Lieferanten aus');
      return;
    }

    setIsLoading(true);
    try {
      await axios.post(
        `${API}/tours`,
        {
          name: newTourName,
          customer_ids: selectedCustomers
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      toast.success('Tour erfolgreich erstellt');
      setCreateDialogOpen(false);
      setNewTourName('');
      setSelectedCustomers([]);
      setCustomerSearchTerm('');
      setSelectedLetter('all');
      fetchTours();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Erstellen der Tour');
    } finally {
      setIsLoading(false);
    }
  };

  const handleOpenEditDialog = (tour) => {
    setTourToEdit(tour);
    setEditTourName(tour.name);
    // Pre-select customers that are already in the tour
    const customerIds = tour.stops.map(stop => stop.customer_id);
    setSelectedCustomers(customerIds);
    setEditDialogOpen(true);
  };

  const handleEditTour = async (e) => {
    e.preventDefault();
    
    if (!editTourName.trim()) {
      toast.error('Bitte geben Sie einen Tour-Namen ein');
      return;
    }
    
    if (selectedCustomers.length === 0) {
      toast.error('Bitte wählen Sie mindestens einen Kunden oder Lieferanten aus');
      return;
    }

    setIsLoading(true);
    try {
      // Create new stops array based on selected customers
      const newStops = selectedCustomers.map((customerId, index) => {
        // Check if this customer already exists in the tour
        const existingStop = tourToEdit.stops.find(s => s.customer_id === customerId);
        
        if (existingStop) {
          // Keep existing stop data (completed status, GPS, etc.)
          return {
            ...existingStop,
            order: index
          };
        } else {
          // Create new stop for newly added customer
          return {
            id: `stop_${Date.now()}_${customerId}`,
            customer_id: customerId,
            order: index,
            completed: false,
            completed_at: null,
            gps_latitude: null,
            gps_longitude: null,
            notes: null,
            photo_data: null
          };
        }
      });

      await axios.put(
        `${API}/tours/${tourToEdit.id}`,
        {
          name: editTourName,
          stops: newStops
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      toast.success('Tour erfolgreich aktualisiert');
      setEditDialogOpen(false);
      setTourToEdit(null);
      setEditTourName('');
      setSelectedCustomers([]);
      setCustomerSearchTerm('');
      setSelectedLetter('all');
      
      // Update selected tour if it's the one being edited
      if (selectedTour?.id === tourToEdit.id) {
        const updatedTourRes = await axios.get(`${API}/tours/${tourToEdit.id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setSelectedTour(updatedTourRes.data);
      }
      
      fetchTours();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Aktualisieren der Tour');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDragEnd = async (result) => {
    if (!result.destination || !selectedTour) return;

    const stops = Array.from(selectedTour.stops);
    const [reorderedStop] = stops.splice(result.source.index, 1);
    stops.splice(result.destination.index, 0, reorderedStop);

    // Update order
    const updatedStops = stops.map((stop, index) => ({
      ...stop,
      order: index
    }));

    // Optimistic update
    setSelectedTour({ ...selectedTour, stops: updatedStops });

    try {
      await axios.put(
        `${API}/tours/${selectedTour.id}`,
        { stops: updatedStops },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      fetchTours();
    } catch (error) {
      toast.error('Fehler beim Aktualisieren der Reihenfolge');
      fetchTours(); // Reload to get correct state
    }
  };

  const handleToggleCustomer = (customerId) => {
    setSelectedCustomers(prev => 
      prev.includes(customerId)
        ? prev.filter(id => id !== customerId)
        : [...prev, customerId]
    );
  };

  const handleStartTour = async (tourId) => {
    try {
      await axios.post(
        `${API}/tours/${tourId}/start`,
        {},
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      toast.success('Tour gestartet');
      fetchTours();
      
      // Select this tour
      const tour = tours.find(t => t.id === tourId);
      if (tour) {
        setSelectedTour({ ...tour, status: 'in_progress' });
      }
    } catch (error) {
      toast.error('Fehler beim Starten der Tour');
    }
  };

  const handleCompleteTour = async (tourId) => {
    try {
      await axios.post(
        `${API}/tours/${tourId}/complete`,
        {},
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      toast.success('Tour abgeschlossen');
      fetchTours();
      setSelectedTour(null);
    } catch (error) {
      toast.error('Fehler beim Abschließen der Tour');
    }
  };

  const handleCompleteStop = async (stop) => {
    if (!selectedTour) return;

    const updatedStop = {
      completed: !stop.completed,
      gps_latitude: currentLocation?.latitude,
      gps_longitude: currentLocation?.longitude
    };

    try {
      await axios.put(
        `${API}/tours/${selectedTour.id}/stops/${stop.id}`,
        updatedStop,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      // Update local state
      const updatedStops = selectedTour.stops.map(s => 
        s.id === stop.id ? { ...s, ...updatedStop, completed_at: new Date() } : s
      );
      setSelectedTour({ ...selectedTour, stops: updatedStops });
      
      toast.success(updatedStop.completed ? 'Stop als erledigt markiert' : 'Stop-Markierung entfernt');
      fetchTours();
    } catch (error) {
      toast.error('Fehler beim Aktualisieren des Stops');
    }
  };

  const openNavigation = (customer) => {
    if (!customer.street || !customer.city) {
      toast.error('Keine Adresse für Navigation verfügbar');
      return;
    }
    
    const address = `${customer.street}, ${customer.zipcode} ${customer.city}, ${customer.country || 'Deutschland'}`;
    return `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(address)}`;
  };

  const handleDeleteTour = async () => {
    if (!tourToDelete) return;

    try {
      await axios.delete(`${API}/tours/${tourToDelete.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Tour erfolgreich gelöscht');
      setDeleteDialogOpen(false);
      setTourToDelete(null);
      if (selectedTour?.id === tourToDelete.id) {
        setSelectedTour(null);
      }
      fetchTours();
    } catch (error) {
      toast.error('Fehler beim Löschen der Tour');
    }
  };

  const getCustomerById = (customerId) => {
    return customers.find(c => c.id === customerId);
  };

  const getTourStats = (tour) => {
    const completedStops = tour.stops.filter(s => s.completed).length;
    const totalStops = tour.stops.length;
    const percentage = totalStops > 0 ? Math.round((completedStops / totalStops) * 100) : 0;
    return { completedStops, totalStops, percentage };
  };

  const getStatusColor = (status) => {
    switch(status) {
      case 'planned': return 'bg-blue-500';
      case 'in_progress': return 'bg-orange-500';
      case 'completed': return 'bg-green-500';
      default: return 'bg-gray-500';
    }
  };

  const getStatusText = (status) => {
    switch(status) {
      case 'planned': return 'Geplant';
      case 'in_progress': return 'In Bearbeitung';
      case 'completed': return 'Abgeschlossen';
      default: return status;
    }
  };

  // Find delivery note or order for a customer/supplier based on customer_id
  const findDocumentForStop = (customerId) => {
    // First check if it's a customer with a delivery note
    const note = deliveryNotes.find(n => n.customer_id === customerId && !n.locked);
    if (note) {
      return { type: 'delivery_note', doc: note };
    }
    
    // Then check if it's a supplier with an order
    const order = orders.find(o => o.supplier_id === customerId && !o.locked);
    if (order) {
      return { type: 'order', doc: order };
    }
    
    return null;
  };

  const navigateToDocument = (customerId) => {
    const docInfo = findDocumentForStop(customerId);
    if (docInfo) {
      if (docInfo.type === 'delivery_note') {
        navigate(`/sign/${docInfo.doc.id}`, { state: { from: 'tours' } });
      } else if (docInfo.type === 'order') {
        navigate(`/orders/${docInfo.doc.id}/sign`, { state: { from: 'tours' } });
      }
    }
  };

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="space-y-6" data-testid="tours">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-4xl font-bold" style={{ fontFamily: 'Space Grotesk, sans-serif', color: '#0A1F33' }}>Tour-Planung</h1>
            <p className="text-gray-600 mt-1">Planen und verwalten Sie Ihre Lieferrouten</p>
          </div>
          <Dialog open={createDialogOpen} onOpenChange={(open) => {
            setCreateDialogOpen(open);
            if (!open) {
              // Reset filters when closing
              setCustomerSearchTerm('');
              setSelectedLetter('all');
            }
          }}>
            <DialogTrigger asChild>
              <Button className="metaxon-primary text-white">
                <Plus className="mr-2 h-4 w-4" />
                Neue Tour erstellen
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>Neue Tour erstellen</DialogTitle>
                <DialogDescription>
                  Wählen Sie Kunden aus und erstellen Sie eine neue Tour
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleCreateTour} className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label htmlFor="tour-name">Tour-Name *</Label>
                  <Input
                    id="tour-name"
                    placeholder="z.B. Montag Vormittag, Route Nord"
                    value={newTourName}
                    onChange={(e) => setNewTourName(e.target.value)}
                    required
                  />
                </div>
                <div className="space-y-3">
                  <Label>{featureSettings.orders_enabled ? 'Kunden & Lieferanten auswählen *' : 'Kunden auswählen *'}</Label>
                  
                  {/* Suchfeld */}
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <Input
                      placeholder={featureSettings.orders_enabled ? "Kunden oder Lieferanten suchen..." : "Kunden suchen..."}
                      value={customerSearchTerm}
                      onChange={(e) => setCustomerSearchTerm(e.target.value)}
                      className="pl-10"
                    />
                  </div>

                  {/* Alphabet-Filter */}
                  <div className="flex flex-wrap gap-1">
                    <Button
                      type="button"
                      variant={selectedLetter === 'all' ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => setSelectedLetter('all')}
                      className={`h-8 w-12 ${selectedLetter === 'all' ? 'metaxon-primary text-white' : ''}`}
                    >
                      Alle
                    </Button>
                    {alphabet.map(letter => (
                      <Button
                        key={letter}
                        type="button"
                        variant={selectedLetter === letter ? 'default' : 'outline'}
                        size="sm"
                        onClick={() => setSelectedLetter(letter)}
                        className={`h-8 w-8 p-0 ${selectedLetter === letter ? 'metaxon-primary text-white' : ''}`}
                      >
                        {letter}
                      </Button>
                    ))}
                  </div>

                  {/* Kunden-Liste */}
                  <div className="border rounded-lg p-4 space-y-2 max-h-96 overflow-y-auto">
                    {filteredCustomers.length === 0 ? (
                      <p className="text-center text-gray-500 py-4">
                        {featureSettings.orders_enabled ? 'Keine Kunden oder Lieferanten gefunden' : 'Keine Kunden gefunden'}
                      </p>
                    ) : (
                      filteredCustomers.map(customer => (
                        <div key={customer.id} className="flex items-start space-x-3 p-2 hover:bg-gray-50 rounded">
                          <Checkbox
                            checked={selectedCustomers.includes(customer.id)}
                            onCheckedChange={() => handleToggleCustomer(customer.id)}
                          />
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <p className="font-medium">{customer.company_name}</p>
                              {featureSettings.orders_enabled && (
                                <span className={`text-xs px-2 py-0.5 rounded ${customer.contactType === 'customer' ? 'bg-blue-100 text-blue-800' : 'bg-purple-100 text-purple-800'}`}>
                                  {customer.contactType === 'customer' ? 'Kunde' : 'Lieferant'}
                                </span>
                              )}
                            </div>
                            <p className="text-sm text-gray-600">
                              {customer.street && `${customer.street}, `}
                              {customer.zipcode} {customer.city}
                            </p>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                  <p className="text-xs text-gray-500">
                    {selectedCustomers.length} Kontakt(e) ausgewählt • {filteredCustomers.length} Kontakt(e) angezeigt
                  </p>
                </div>
                <Button type="submit" className="w-full metaxon-primary text-white" disabled={isLoading}>
                  Tour erstellen
                </Button>
              </form>
            </DialogContent>
          </Dialog>

          {/* Edit Tour Dialog */}
          <Dialog open={editDialogOpen} onOpenChange={(open) => {
            setEditDialogOpen(open);
            if (!open) {
              setTourToEdit(null);
              setEditTourName('');
              setSelectedCustomers([]);
              setCustomerSearchTerm('');
              setSelectedLetter('all');
            }
          }}>
            <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>Tour bearbeiten</DialogTitle>
                <DialogDescription>
                  Ändern Sie den Namen oder fügen Sie Kunden hinzu/entfernen Sie Kunden
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleEditTour} className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label htmlFor="edit-tour-name">Tour-Name *</Label>
                  <Input
                    id="edit-tour-name"
                    placeholder="z.B. Montag Vormittag, Route Nord"
                    value={editTourName}
                    onChange={(e) => setEditTourName(e.target.value)}
                    required
                  />
                </div>
                <div className="space-y-3">
                  <Label>{featureSettings.orders_enabled ? 'Kunden & Lieferanten verwalten *' : 'Kunden verwalten *'}</Label>
                  
                  {/* Suchfeld */}
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <Input
                      placeholder={featureSettings.orders_enabled ? "Kunden oder Lieferanten suchen..." : "Kunden suchen..."}
                      value={customerSearchTerm}
                      onChange={(e) => setCustomerSearchTerm(e.target.value)}
                      className="pl-10"
                    />
                  </div>

                  {/* Alphabet-Filter */}
                  <div className="flex flex-wrap gap-1">
                    <Button
                      type="button"
                      variant={selectedLetter === 'all' ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => setSelectedLetter('all')}
                      className={`h-8 w-12 ${selectedLetter === 'all' ? 'metaxon-primary text-white' : ''}`}
                    >
                      Alle
                    </Button>
                    {alphabet.map(letter => (
                      <Button
                        key={letter}
                        type="button"
                        variant={selectedLetter === letter ? 'default' : 'outline'}
                        size="sm"
                        onClick={() => setSelectedLetter(letter)}
                        className={`h-8 w-8 p-0 ${selectedLetter === letter ? 'metaxon-primary text-white' : ''}`}
                      >
                        {letter}
                      </Button>
                    ))}
                  </div>

                  {/* Kunden-Liste mit Hinzufügen/Entfernen Icons */}
                  <div className="border rounded-lg p-4 space-y-2 max-h-96 overflow-y-auto">
                    {filteredCustomers.length === 0 ? (
                      <p className="text-center text-gray-500 py-4">
                        {featureSettings.orders_enabled ? 'Keine Kunden oder Lieferanten gefunden' : 'Keine Kunden gefunden'}
                      </p>
                    ) : (
                      filteredCustomers.map(customer => {
                        const isSelected = selectedCustomers.includes(customer.id);
                        return (
                          <div key={customer.id} className="flex items-start space-x-3 p-2 hover:bg-gray-50 rounded">
                            <Checkbox
                              checked={isSelected}
                              onCheckedChange={() => handleToggleCustomer(customer.id)}
                            />
                            <div className="flex-1">
                              <div className="flex items-center gap-2 flex-wrap">
                                <p className="font-medium">{customer.company_name}</p>
                                {featureSettings.orders_enabled && (
                                  <span className={`text-xs px-2 py-0.5 rounded ${customer.contactType === 'customer' ? 'bg-blue-100 text-blue-800' : 'bg-purple-100 text-purple-800'}`}>
                                    {customer.contactType === 'customer' ? 'Kunde' : 'Lieferant'}
                                  </span>
                                )}
                                {isSelected ? (
                                  <span className="inline-flex items-center px-2 py-0.5 rounded text-xs bg-green-100 text-green-700">
                                    <UserPlus className="h-3 w-3 mr-1" />
                                    In Tour
                                  </span>
                                ) : (
                                  <span className="inline-flex items-center px-2 py-0.5 rounded text-xs bg-gray-100 text-gray-600">
                                    <UserMinus className="h-3 w-3 mr-1" />
                                    Nicht in Tour
                                  </span>
                                )}
                              </div>
                              <p className="text-sm text-gray-600">
                                {customer.street && `${customer.street}, `}
                                {customer.zipcode} {customer.city}
                              </p>
                            </div>
                          </div>
                        );
                      })
                    )}
                  </div>
                  <p className="text-xs text-gray-500">
                    {selectedCustomers.length} Kontakt(e) in Tour • {filteredCustomers.length} Kontakt(e) angezeigt
                  </p>
                </div>
                <div className="flex gap-2">
                  <Button 
                    type="button" 
                    variant="outline" 
                    className="flex-1"
                    onClick={() => setEditDialogOpen(false)}
                  >
                    Abbrechen
                  </Button>
                  <Button 
                    type="submit" 
                    className="flex-1 metaxon-primary text-white" 
                    disabled={isLoading}
                  >
                    Änderungen speichern
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Tour List */}
          <div className="lg:col-span-1 space-y-4">
            <Card className="glass-effect border-0">
              <CardHeader>
                <CardTitle className="text-sm font-medium">Meine Touren</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                {tours.length === 0 ? (
                  <p className="text-sm text-gray-500 text-center py-4">Noch keine Touren erstellt</p>
                ) : (
                  tours.map(tour => {
                    const stats = getTourStats(tour);
                    return (
                      <div
                        key={tour.id}
                        onClick={() => setSelectedTour(tour)}
                        className={`p-3 rounded-lg border-2 cursor-pointer transition-all ${
                          selectedTour?.id === tour.id 
                            ? 'border-blue-500 bg-blue-50' 
                            : 'border-gray-200 hover:border-blue-300'
                        }`}
                      >
                        <div className="flex items-start justify-between mb-2">
                          <div className="flex-1">
                            <p className="font-medium text-sm">{tour.name}</p>
                            <div className="flex items-center gap-2 mt-1">
                              <span className={`text-xs px-2 py-0.5 rounded-full text-white ${getStatusColor(tour.status)}`}>
                                {getStatusText(tour.status)}
                              </span>
                              <span className="text-xs text-gray-500">
                                {stats.completedStops}/{stats.totalStops} Stops
                              </span>
                            </div>
                          </div>
                          <div className="flex gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleOpenEditDialog(tour);
                              }}
                              className="h-8 w-8 p-0"
                              title="Tour bearbeiten"
                            >
                              <Pencil className="h-4 w-4 text-blue-500" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={(e) => {
                                e.stopPropagation();
                                setTourToDelete(tour);
                                setDeleteDialogOpen(true);
                              }}
                              className="h-8 w-8 p-0"
                              title="Tour löschen"
                            >
                              <Trash2 className="h-4 w-4 text-red-500" />
                            </Button>
                          </div>
                        </div>
                        {tour.status === 'in_progress' && (
                          <div className="mt-2">
                            <div className="w-full bg-gray-200 rounded-full h-1.5">
                              <div 
                                className="bg-orange-500 h-1.5 rounded-full transition-all" 
                                style={{ width: `${stats.percentage}%` }}
                              ></div>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })
                )}
              </CardContent>
            </Card>
          </div>

          {/* Tour Details */}
          <div className="lg:col-span-2">
            {!selectedTour ? (
              <Card className="glass-effect border-0">
                <CardContent className="pt-20 pb-20 text-center">
                  <MapPin className="h-16 w-16 mx-auto text-gray-300 mb-4" />
                  <p className="text-gray-500">Wählen Sie eine Tour aus oder erstellen Sie eine neue</p>
                </CardContent>
              </Card>
            ) : (
              <Card className="glass-effect border-0">
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle>{selectedTour.name}</CardTitle>
                      <CardDescription className="mt-1">
                        <span className={`inline-block px-2 py-1 rounded-full text-xs text-white ${getStatusColor(selectedTour.status)}`}>
                          {getStatusText(selectedTour.status)}
                        </span>
                      </CardDescription>
                    </div>
                    <div className="flex gap-2">
                      {selectedTour.status === 'planned' && (
                        <Button
                          onClick={() => handleStartTour(selectedTour.id)}
                          className="metaxon-primary text-white"
                        >
                          <Play className="mr-2 h-4 w-4" />
                          Tour starten
                        </Button>
                      )}
                      {selectedTour.status === 'in_progress' && (
                        <Button
                          onClick={() => handleCompleteTour(selectedTour.id)}
                          className="bg-green-600 hover:bg-green-700 text-white"
                        >
                          <CheckCircle className="mr-2 h-4 w-4" />
                          Tour abschließen
                        </Button>
                      )}
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <DragDropContext onDragEnd={handleDragEnd}>
                    <Droppable droppableId="tour-stops">
                      {(provided) => (
                        <div
                          {...provided.droppableProps}
                          ref={provided.innerRef}
                          className="space-y-3"
                        >
                          {selectedTour.stops
                            .sort((a, b) => a.order - b.order)
                            .map((stop, index) => {
                              const customer = getCustomerById(stop.customer_id);
                              if (!customer) return null;

                              return (
                                <Draggable
                                  key={stop.id}
                                  draggableId={stop.id}
                                  index={index}
                                  isDragDisabled={selectedTour.status === 'completed'}
                                >
                                  {(provided, snapshot) => (
                                    <div
                                      ref={provided.innerRef}
                                      {...provided.draggableProps}
                                      className={`p-4 rounded-lg border-2 ${
                                        stop.completed 
                                          ? 'bg-green-50 border-green-200' 
                                          : snapshot.isDragging
                                          ? 'bg-blue-50 border-blue-300 shadow-lg'
                                          : 'bg-white border-gray-200'
                                      }`}
                                    >
                                      <div className="flex items-start gap-3">
                                        <div {...provided.dragHandleProps} className="mt-1">
                                          <GripVertical className="h-5 w-5 text-gray-400" />
                                        </div>
                                        
                                        <div className="flex items-start gap-3 flex-1">
                                          <Checkbox
                                            checked={stop.completed}
                                            onCheckedChange={() => handleCompleteStop(stop)}
                                            disabled={selectedTour.status === 'completed'}
                                            className="mt-1"
                                          />
                                          
                                          <div className="flex-1">
                                            <div className="flex items-start justify-between">
                                              <div>
                                                <p className={`font-medium ${stop.completed ? 'line-through text-gray-500' : ''}`}>
                                                  {index + 1}. {customer.company_name}
                                                </p>
                                                <p className="text-sm text-gray-600 mt-1">
                                                  {customer.street}<br />
                                                  {customer.zipcode} {customer.city}
                                                  {customer.country && `, ${customer.country}`}
                                                </p>
                                                {stop.completed_at && (
                                                  <div className="flex items-center gap-2 mt-2 text-xs text-gray-500">
                                                    <Clock className="h-3 w-3" />
                                                    {new Date(stop.completed_at).toLocaleString('de-DE')}
                                                  </div>
                                                )}
                                              </div>
                                              
                                              {/* Action Buttons: Route & Lieferschein nebeneinander */}
                                              {selectedTour.status !== 'completed' && (
                                                <div className="flex gap-2 mt-3">
                                                  {/* Lieferschein/Bestellung Button */}
                                                  {(() => {
                                                    const docInfo = findDocumentForStop(stop.customer_id);
                                                    
                                                    if (!docInfo) {
                                                      return null;
                                                    }
                                                    
                                                    const isSigned = docInfo.doc.signed;
                                                    const docType = docInfo.type === 'delivery_note' ? 'Lieferschein' : 'Bestellung';
                                                    
                                                    return (
                                                      <Button
                                                        size="sm"
                                                        onClick={() => navigateToDocument(stop.customer_id)}
                                                        variant="outline"
                                                        className={`${
                                                          isSigned 
                                                            ? 'border-green-500 text-green-700 hover:bg-green-50' 
                                                            : 'border-orange-500 text-orange-700 hover:bg-orange-50'
                                                        }`}
                                                      >
                                                        {isSigned ? (
                                                          <>
                                                            <CheckCircle className="h-4 w-4 mr-2" />
                                                            {docType}
                                                          </>
                                                        ) : (
                                                          <>
                                                            <FileText className="h-4 w-4 mr-2" />
                                                            {docType}
                                                          </>
                                                        )}
                                                      </Button>
                                                    );
                                                  })()}
                                                  
                                                  {/* Route Button - programmatischer Link-Klick für Firefox COOP-Workaround */}
                                                  <Button
                                                    size="sm"
                                                    variant="outline"
                                                    onClick={() => {
                                                      const url = openNavigation(customer);
                                                      if (url) {
                                                        // Workaround für Firefox COOP-Problem
                                                        const link = document.createElement('a');
                                                        link.href = url;
                                                        link.target = '_blank';
                                                        link.rel = 'noopener noreferrer';
                                                        document.body.appendChild(link);
                                                        link.click();
                                                        document.body.removeChild(link);
                                                      }
                                                    }}
                                                  >
                                                    <Navigation className="h-4 w-4 mr-2" />
                                                    Route
                                                  </Button>
                                                </div>
                                              )}
                                            </div>
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                  )}
                                </Draggable>
                              );
                            })}
                          {provided.placeholder}
                        </div>
                      )}
                    </Droppable>
                  </DragDropContext>
                </CardContent>
              </Card>
            )}
          </div>
        </div>

        {/* Delete Confirmation Dialog */}
        <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Tour wirklich löschen?</AlertDialogTitle>
              <AlertDialogDescription>
                Möchten Sie die Tour "{tourToDelete?.name}" wirklich löschen? 
                Diese Aktion kann nicht rückgängig gemacht werden.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Abbrechen</AlertDialogCancel>
              <AlertDialogAction
                onClick={handleDeleteTour}
                className="bg-red-600 hover:bg-red-700"
              >
                Tour löschen
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </Layout>
  );
}
